/**
 * Dropdown highlight index for {@link TypeaheadField}.
 * Only a user-typed query implies the first row — a prefilled label on open
 * does not. Enter then unsets unless the user hovered, arrowed, or typed.
 */
export const resolveTypeaheadActiveIndex = (
  matches: readonly { key: string }[],
  activeKey: string | null,
  hasSearchQuery: boolean,
): number => {
  if (matches.length === 0) return -1;
  if (activeKey) {
    const index = matches.findIndex((match) => match.key === activeKey);
    if (index >= 0) return index;
  }
  if (!hasSearchQuery) return -1;
  return 0;
};

/** Fallback when the field has not measured yet. */
const TYPEAHEAD_DROPDOWN_FALLBACK_MIN_WIDTH_PX = 240;

/**
 * Dropdown width matches the field. Unmeasured fields fall back to 240px.
 */
export const getTypeaheadDropdownWidthConstraints = (
  anchorWidth: number,
  fallbackMinWidth = TYPEAHEAD_DROPDOWN_FALLBACK_MIN_WIDTH_PX,
): { minWidth: number; maxWidth: number } => {
  const width = anchorWidth > 0 ? anchorWidth : fallbackMinWidth;
  return { minWidth: width, maxWidth: width };
};

export type TypeaheadEnterAction =
  { type: 'pick'; key: string } | { type: 'unset' } | { type: 'close' };

type TypeaheadEnterInput = {
  activeItemKey: string | undefined;
  highlightCommitted: boolean;
  /** True only after the user changed the field — not a prefilled open label. */
  userTypedSearch: boolean;
  /** True when the input has no non-whitespace query. */
  queryEmpty: boolean;
};

/**
 * Enter in {@link TypeaheadField}: pick after hover/arrows or a typed search
 * that has a match. Empty the field + Enter unsets. Bare open, or a typed
 * query with no match, closes and keeps the prior pick.
 */
export const resolveTypeaheadEnterAction = ({
  activeItemKey,
  highlightCommitted,
  userTypedSearch,
  queryEmpty,
}: TypeaheadEnterInput): TypeaheadEnterAction => {
  if (highlightCommitted && activeItemKey) {
    return { type: 'pick', key: activeItemKey };
  }
  if (userTypedSearch && activeItemKey) {
    return { type: 'pick', key: activeItemKey };
  }
  if (queryEmpty && userTypedSearch) {
    return { type: 'unset' };
  }
  return { type: 'close' };
};
